import { Button, PocketStatement } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsSkillsMessages } from "@/i18n/messages/settings-skills";
import { useSettingsNav } from "./settings-navigation";
import { useSkillPage } from "./skill-page-state";
import {
  DetailList,
  sectionsFor,
  StepList,
  textList,
} from "./work-skill-content";

export function SkillUnavailable({
  loading,
  retry,
}: {
  loading: boolean;
  retry: () => void;
}) {
  const t = useT(settingsSkillsMessages);
  const nav = useSettingsNav();
  return (
    <div className="px-2 py-3" data-testid="skill-unavailable">
      <p role="status" className="text-appkit-body text-muted">
        {loading ? t("loadingSkill") : t("skillUnavailable")}
      </p>
      {!loading && (
        <div className="mt-2 flex gap-2">
          <Button size="sm" data-testid="skill-retry" onClick={retry}>
            {t("retry")}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            data-testid="skill-back"
            onClick={() => nav.open({ page: "skills" })}
          >
            {t("skills")}
          </Button>
        </div>
      )}
    </div>
  );
}
export function SkillPage({
  skillId,
  kind,
}: {
  skillId: string;
  kind?: string;
}) {
  const t = useT(settingsSkillsMessages);
  const nav = useSettingsNav();
  const state = useSkillPage(skillId, kind);
  if (state.loading || state.error)
    return <SkillUnavailable loading={state.loading} retry={state.retry} />;
  if (state.agent)
    return (
      <PocketStatement
        size="panel"
        title={state.agent.name}
        line={state.agent.description}
        testId="agent-skill-detail"
      />
    );
  const detail = state.detail;
  if (!detail) return <SkillUnavailable loading={false} retry={state.retry} />;
  const title = String(detail.metadata.title ?? detail.name);
  const metadata = { ...state.library?.metadata, ...detail.metadata };
  const sections = sectionsFor(detail);
  const action = state.personal ? (
    <Button
      size="sm"
      variant="secondary"
      data-testid="work-skill-open-personal"
      onClick={() =>
        nav.push({
          page: "skills-skill",
          args: {
            skillId: state.personal!.id,
            title: String(
              state.personal!.metadata.title ?? state.personal!.name,
            ),
            kind: "work",
          },
        })
      }
    >
      {t("openYourVersion")}
    </Button>
  ) : detail.shared || detail.metadata.adapts ? (
    <Button
      size="sm"
      variant="secondary"
      data-testid="work-skill-customize"
      onClick={() =>
        nav.push({
          page: "skills-customize",
          args: { skillId, title, kind: "work" },
        })
      }
    >
      {detail.shared ? t("customizeForMe") : t("editMyVersion")}
    </Button>
  ) : undefined;
  return (
    <div className="space-y-4" data-testid="work-skill-detail">
      <PocketStatement
        size="panel"
        title={title}
        line={String(metadata.summary ?? detail.description ?? "")}
        action={action}
      />
      <div className="space-y-4 px-2">
        <DetailList
          title={t("whatYouNeed")}
          items={textList(metadata.inputs)}
        />
        {sections.some((section) => section.steps.length > 0) && (
          <StepList sections={sections} />
        )}
        <DetailList
          title={t("whatYouGet")}
          items={textList(metadata.outputs)}
        />
        <DetailList
          title={t("qualityChecks")}
          items={textList(metadata.quality_checks)}
        />
        <DetailList
          title={t("watchOutFor")}
          items={textList(metadata.pitfalls)}
        />
        <DetailList title={t("sources")} items={textList(metadata.sources)} />
      </div>
    </div>
  );
}
